/** Cognito error fields used at the browser boundary. */
export interface CognitoFailure {
  readonly code?: string;
  readonly message?: string;
}

export const cognitoFailure = (error: unknown): CognitoFailure =>
  error !== null && typeof error === "object" ? (error as CognitoFailure) : {};

/**
 * Existence-sensitive failures are intentionally collapsed.
 *
 * Sign-in and password recovery are two views of the same account directory;
 * neither may become a way to ask whether an address is registered.
 */
export const isHiddenAccountFailure = (error: unknown): boolean => {
  const code = cognitoFailure(error).code;
  return code === "UserNotFoundException" || code === "NotAuthorizedException";
};

export const confirmationFailureMessage = (error: unknown): string => {
  switch (cognitoFailure(error).code) {
    case "CodeMismatchException":
      return "That confirmation code is not correct.";
    case "ExpiredCodeException":
      return "That confirmation code has expired. Request a new one.";
    case "LimitExceededException":
      return "Too many attempts. Wait a little before trying again.";
    default:
      return "We could not confirm that code. Please try again.";
  }
};

export const resetFailureMessage = (error: unknown): string => {
  switch (cognitoFailure(error).code) {
    case "CodeMismatchException":
      return "That reset code is not correct.";
    case "ExpiredCodeException":
      return "That reset code has expired. Request a new one.";
    case "InvalidPasswordException":
      return "Use at least 12 characters with upper-case, lower-case and a number.";
    case "LimitExceededException":
      return "Too many attempts. Wait a little before trying again.";
    default:
      return "We could not reset the password. Please try again.";
  }
};
